<script setup lang="ts">
import { computed, ref } from 'vue'
import { useQuery, useQueryClient, useMutation } from '@tanstack/vue-query'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import { useI18n } from 'vue-i18n'
import { z } from 'zod'
import { zodResolver } from '@primevue/forms/resolvers/zod'
import { Form, type FormSubmitEvent } from '@primevue/forms'
import { jwtDecode } from 'jwt-decode'
import { Roles } from '@/constants.ts'
import jwtService from '@/services/jwtService.ts'
import staffHttpService, {
  type StaffMember,
  type StaffRole,
  type StaffSecurity,
} from '@/services/http/staffHttpService.ts'
import type { JwtPayload } from '@/types/auth'

definePage({
  meta: {
    allowedRoles: [Roles.Admin],
  },
})

const { t, locale } = useI18n()
const toast = useToast()
const confirm = useConfirm()
const queryClient = useQueryClient()

// Себя нельзя блокировать и менять себе роль (бэк тоже запрещает — защита последнего админа).
const token = jwtService.getToken()
const currentUserId = token ? jwtDecode<JwtPayload>(token).sub : null

const staffQuery = useQuery({
  queryKey: computed(() => ['admin', 'staff', locale.value]),
  queryFn: () => staffHttpService.getStaff(),
})

const staff = computed<StaffMember[]>(() => staffQuery.data.value ?? [])

function invalidate() {
  queryClient.invalidateQueries({ queryKey: ['admin', 'staff'] })
}

function isSelf(member: StaffMember): boolean {
  return member.id === currentUserId
}

// Подсветка строки текущего пользователя (вместо метки в ячейке имени).
function rowClass(member: StaffMember): string {
  return isSelf(member) ? 'staff-self-row' : ''
}

function isLocked(member: StaffMember): boolean {
  return member.lockoutEnd != null && new Date(member.lockoutEnd) > new Date()
}

// Ручная блокировка ставит LockoutEnd = DateTimeOffset.MaxValue (год 9999) — бессрочная;
// временная (брутфорс) имеет конкретное время окончания.
function isIndefiniteLock(member: StaffMember): boolean {
  return member.lockoutEnd != null && new Date(member.lockoutEnd).getUTCFullYear() >= 9999
}

function lockoutLabel(member: StaffMember): string {
  if (!isLocked(member)) return t('staff.active')
  if (isIndefiniteLock(member)) return t('staff.lockedByAdmin')
  const until = new Date(member.lockoutEnd!).toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  })
  return t('staff.lockedUntil', { time: until })
}

function roleLabel(role: StaffRole): string {
  return role === 'Admin' || role === 'Manager' ? t(`staff.roles.${role}`) : role
}

const roleOptions = computed(() => [
  { label: t('staff.roles.Admin'), value: 'Admin' },
  { label: t('staff.roles.Manager'), value: 'Manager' },
])

function notify(success: boolean, detail: string) {
  toast.add({
    severity: success ? 'success' : 'error',
    summary: success ? t('common.success') : t('common.error'),
    detail,
    life: 3000,
  })
}

// --- Валидация (zod, как в доноре career-guidance) ---

const passwordRules = computed(() =>
  z
    .string()
    .min(6, { message: t('staff.validation.passwordMin') })
    .refine((value) => /[a-z]/.test(value), { message: t('staff.validation.passwordLower') })
    .refine((value) => /[A-Z]/.test(value), { message: t('staff.validation.passwordUpper') })
    .refine((value) => /\d+/.test(value), { message: t('staff.validation.passwordDigit') })
    .refine((value) => /\W/.test(value), { message: t('staff.validation.passwordSpecial') }),
)

// --- Создание ---

const showCreateDialog = ref(false)

const createSchema = computed(() =>
  z
    .object({
      // trim ДО валидации: Zod z.email() не обрезает пробелы, и хвостовой пробел из
      // копипаста ронял валидный адрес ещё на клиенте (POST не уходил). pipe отдаёт обрезанное.
      email: z
        .string()
        .trim()
        .pipe(z.email({ message: t('staff.validation.emailInvalid') })),
      name: z.string().optional(),
      role: z.enum(['Admin', 'Manager'], { message: t('staff.validation.roleRequired') }),
      password: passwordRules.value,
      confirmPassword: z.string().min(1, { message: t('staff.validation.confirmRequired') }),
    })
    .refine((d) => d.password === d.confirmPassword, {
      message: t('staff.validation.passwordsMismatch'),
      path: ['confirmPassword'],
    }),
)

const createResolver = computed(() => zodResolver(createSchema.value))

const createInitialValues = {
  email: '',
  name: '',
  role: 'Manager',
  password: '',
  confirmPassword: '',
}

interface CreateValues {
  email: string
  name?: string
  role: StaffRole
  password: string
  confirmPassword: string
}

const createStaff = async ({ valid, values }: FormSubmitEvent) => {
  if (!valid) return

  // values валидированы zod-резолвером — форма соответствует CreateValues.
  const form = values as CreateValues
  try {
    await staffHttpService.createStaff({
      email: form.email,
      name: (form.name ?? '').trim() || null,
      password: form.password,
      role: form.role,
    })
    notify(true, t('staff.toasts.created'))
    showCreateDialog.value = false
    invalidate()
  } catch {
    notify(false, t('staff.toasts.createFailed'))
  }
}

// --- Редактирование ---

const showEditDialog = ref(false)
const selectedMember = ref<StaffMember | null>(null)

const editSchema = computed(() =>
  z.object({
    email: z
      .string()
      .trim()
      .pipe(z.email({ message: t('staff.validation.emailInvalid') })),
    name: z.string().nullable().optional(),
  }),
)
const editResolver = computed(() => zodResolver(editSchema.value))

const editInitialValues = computed(() => ({
  email: selectedMember.value?.email ?? '',
  name: selectedMember.value?.name ?? '',
}))

function openEditDialog(member: StaffMember) {
  selectedMember.value = member
  showEditDialog.value = true
}

const saveEdit = async ({ valid, values }: FormSubmitEvent) => {
  if (!valid || selectedMember.value == null) return

  const form = values as { email: string; name?: string | null }
  try {
    await staffHttpService.updateStaff(selectedMember.value.id, {
      email: form.email,
      name: (form.name ?? '').trim() || null,
    })
    notify(true, t('staff.toasts.updated'))
    showEditDialog.value = false
    invalidate()
  } catch {
    notify(false, t('staff.toasts.updateFailed'))
  }
}

// --- Смена пароля ---

const showPasswordDialog = ref(false)

const passwordSchema = computed(() =>
  z
    .object({
      password: passwordRules.value,
      confirmPassword: z.string().min(1, { message: t('staff.validation.confirmRequired') }),
    })
    .refine((d) => d.password === d.confirmPassword, {
      message: t('staff.validation.passwordsMismatch'),
      path: ['confirmPassword'],
    }),
)
const passwordResolver = computed(() => zodResolver(passwordSchema.value))

const passwordInitialValues = { password: '', confirmPassword: '' }

function openPasswordDialog(member: StaffMember) {
  selectedMember.value = member
  showPasswordDialog.value = true
}

const savePassword = async ({ valid, values }: FormSubmitEvent) => {
  if (!valid || selectedMember.value == null) return

  const form = values as { password: string; confirmPassword: string }
  try {
    await staffHttpService.updateStaffPassword(selectedMember.value.id, form.password)
    notify(true, t('staff.toasts.passwordUpdated'))
    showPasswordDialog.value = false
  } catch {
    notify(false, t('staff.toasts.passwordFailed'))
  }
}

// --- Смена роли ---

const showRoleDialog = ref(false)

const roleSchema = computed(() =>
  z.object({
    role: z.enum(['Admin', 'Manager'], { message: t('staff.validation.roleRequired') }),
  }),
)
const roleResolver = computed(() => zodResolver(roleSchema.value))

const roleInitialValues = computed(() => ({
  role: selectedMember.value?.role ?? 'Manager',
}))

function openRoleDialog(member: StaffMember) {
  selectedMember.value = member
  showRoleDialog.value = true
}

const saveRole = async ({ valid, values }: FormSubmitEvent) => {
  if (!valid || selectedMember.value == null) return

  const form = values as { role: StaffRole }
  try {
    await staffHttpService.updateStaffRole(selectedMember.value.id, form.role)
    notify(true, t('staff.toasts.roleUpdated'))
    showRoleDialog.value = false
    invalidate()
  } catch {
    notify(false, t('staff.toasts.roleFailed'))
  }
}

// --- Блокировка ---

const lockoutMutation = useMutation({
  mutationFn: ({ id, lock }: { id: string; lock: boolean }) =>
    staffHttpService.setStaffLockout(id, lock),
  onSuccess: () => {
    notify(true, t('staff.toasts.lockUpdated'))
    invalidate()
  },
  onError: () => notify(false, t('staff.toasts.lockFailed')),
})

function toggleLockout(member: StaffMember) {
  const lock = !isLocked(member)
  const who = member.name ?? member.email ?? ''
  confirm.require({
    header: lock ? t('staff.lockConfirm.lockHeader') : t('staff.lockConfirm.unlockHeader'),
    message: lock
      ? t('staff.lockConfirm.lockMessage', { name: who })
      : t('staff.lockConfirm.unlockMessage', { name: who }),
    icon: 'pi pi-exclamation-triangle',
    rejectProps: { label: t('common.cancel'), severity: 'secondary', outlined: true },
    acceptProps: {
      label: lock ? t('staff.actions.lock') : t('staff.actions.unlock'),
      severity: lock ? 'danger' : 'warn',
    },
    accept: () => lockoutMutation.mutate({ id: member.id, lock }),
  })
}

// Карточка безопасности/сессий сотрудника (данные из OpenIddict-токенов + Identity).
const securityDialogVisible = ref(false)
const securityMember = ref<StaffMember | null>(null)
const securityData = ref<StaffSecurity | null>(null)
const securityLoading = ref(false)

async function openSecurity(member: StaffMember) {
  securityMember.value = member
  securityData.value = null
  securityDialogVisible.value = true
  securityLoading.value = true
  try {
    securityData.value = await staffHttpService.getStaffSecurity(member.id)
  } catch {
    notify(false, t('staff.security.loadFailed'))
  } finally {
    securityLoading.value = false
  }
}

const revokeMutation = useMutation({
  mutationFn: (id: string) => staffHttpService.revokeStaffSessions(id),
  onSuccess: async (res) => {
    notify(true, t('staff.security.revoked', { count: res.revoked }))
    if (securityMember.value) {
      securityData.value = await staffHttpService.getStaffSecurity(securityMember.value.id)
    }
  },
  onError: () => notify(false, t('staff.security.revokeFailed')),
})

function confirmRevoke() {
  const member = securityMember.value
  if (!member) return
  confirm.require({
    header: t('staff.security.revokeConfirm.header'),
    message: t('staff.security.revokeConfirm.message', { name: member.name ?? member.email }),
    icon: 'pi pi-exclamation-triangle',
    rejectProps: { label: t('common.cancel'), severity: 'secondary', outlined: true },
    acceptProps: { label: t('staff.security.revokeBtn'), severity: 'danger' },
    accept: () => revokeMutation.mutate(member.id),
  })
}

function formatDateTime(value: string | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleString('ru-RU', { dateStyle: 'medium', timeStyle: 'short' })
}
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <div class="flex items-center justify-between w-full">
      <h1 class="text-xl font-bold">{{ t('staff.title') }}</h1>
      <Button
        :label="t('staff.add')"
        icon="pi pi-plus"
        data-test="staff-add"
        @click="showCreateDialog = true"
      />
    </div>

    <DataTable
      :value="staff"
      :loading="staffQuery.isLoading.value"
      data-key="id"
      class="w-full"
      :row-class="rowClass"
      table-style="min-width: 73rem; table-layout: fixed"
    >
      <template #empty>
        <div class="text-sm text-surface-500 py-4">{{ t('staff.empty') }}</div>
      </template>

      <Column :header="t('staff.columns.name')" style="width: 12rem">
        <template #body="{ data }">
          <span class="block truncate" :title="data.name ?? '—'">{{ data.name ?? '—' }}</span>
        </template>
      </Column>
      <Column :header="t('staff.columns.email')" style="width: 15rem">
        <template #body="{ data }">
          <span class="block truncate" :title="data.email">{{ data.email }}</span>
        </template>
      </Column>
      <Column :header="t('staff.columns.role')" style="width: 8rem">
        <template #body="{ data }">
          <span data-test="staff-role">{{ roleLabel(data.role) }}</span>
        </template>
      </Column>
      <Column :header="t('staff.columns.emailConfirmed')" style="width: 8rem">
        <template #body="{ data }">
          <Tag
            :value="data.emailConfirmed ? t('staff.confirmedYes') : t('staff.confirmedNo')"
            :severity="data.emailConfirmed ? 'success' : 'secondary'"
          />
        </template>
      </Column>
      <Column :header="t('staff.columns.lockout')" style="width: 15rem">
        <template #body="{ data }">
          <Tag
            :value="lockoutLabel(data)"
            :severity="isLocked(data) ? 'danger' : 'success'"
            data-test="staff-lockout-state"
          />
        </template>
      </Column>
      <Column :header="t('staff.columns.actions')" style="width: 15rem">
        <template #body="{ data }">
          <div class="flex gap-2">
            <Button
              size="small"
              severity="help"
              outlined
              icon="pi pi-shield"
              :title="t('staff.security.title')"
              :aria-label="t('staff.security.title')"
              data-test="staff-security"
              @click="openSecurity(data)"
            />
            <Button
              size="small"
              outlined
              icon="pi pi-pencil"
              :title="t('staff.actions.edit')"
              :aria-label="t('staff.actions.edit')"
              data-test="staff-edit"
              @click="openEditDialog(data)"
            />
            <Button
              size="small"
              severity="secondary"
              outlined
              icon="pi pi-key"
              :title="t('staff.actions.password')"
              :aria-label="t('staff.actions.password')"
              data-test="staff-password"
              @click="openPasswordDialog(data)"
            />
            <Button
              v-if="!isSelf(data)"
              size="small"
              severity="secondary"
              outlined
              icon="pi pi-user-edit"
              :title="t('staff.actions.role')"
              :aria-label="t('staff.actions.role')"
              data-test="staff-role-change"
              @click="openRoleDialog(data)"
            />
            <Button
              v-if="!isSelf(data)"
              size="small"
              :severity="isLocked(data) ? 'warn' : 'danger'"
              outlined
              :icon="isLocked(data) ? 'pi pi-lock-open' : 'pi pi-lock'"
              :title="isLocked(data) ? t('staff.actions.unlock') : t('staff.actions.lock')"
              :aria-label="isLocked(data) ? t('staff.actions.unlock') : t('staff.actions.lock')"
              :loading="lockoutMutation.isPending.value"
              data-test="staff-lockout"
              @click="toggleLockout(data)"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <Dialog
      v-model:visible="securityDialogVisible"
      :header="t('staff.security.title')"
      modal
      dismissable-mask
      class="w-[90vw] sm:w-[440px]"
    >
      <div v-if="securityLoading" class="py-6 text-center text-surface-500">
        {{ t('common.loading') }}
      </div>
      <div v-else-if="securityData" class="flex flex-col gap-3">
        <div class="text-sm text-surface-500">{{ securityMember?.email }}</div>
        <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt class="text-surface-500">{{ t('staff.security.created') }}</dt>
          <dd data-test="security-created">{{ formatDateTime(securityData.createdAt) }}</dd>
          <dt class="text-surface-500">{{ t('staff.security.lastLogin') }}</dt>
          <dd data-test="security-last-login">
            {{
              securityData.lastLoginAt
                ? formatDateTime(securityData.lastLoginAt)
                : t('staff.security.never')
            }}
          </dd>
          <dt class="text-surface-500">{{ t('staff.security.activeSessions') }}</dt>
          <dd data-test="security-sessions">{{ securityData.activeSessions }}</dd>
          <dt class="text-surface-500">{{ t('staff.security.failedAttempts') }}</dt>
          <dd data-test="security-failed">{{ securityData.accessFailedCount }}</dd>
        </dl>
        <p class="text-xs text-surface-500 pt-1">{{ t('staff.security.revokeHint') }}</p>
        <div class="flex justify-end">
          <Button
            :label="t('staff.security.revokeBtn')"
            icon="pi pi-power-off"
            severity="danger"
            outlined
            size="small"
            :loading="revokeMutation.isPending.value"
            :disabled="securityData.activeSessions === 0"
            data-test="staff-revoke-sessions"
            @click="confirmRevoke"
          />
        </div>
      </div>
    </Dialog>

    <Dialog
      v-model:visible="showCreateDialog"
      :header="t('staff.dialogs.create')"
      modal
      dismissable-mask
      class="w-[90vw] sm:w-[600px]"
    >
      <Form
        v-slot="$form"
        :resolver="createResolver"
        :initial-values="createInitialValues"
        class="flex flex-col gap-4"
        @submit="createStaff"
      >
        <div class="flex flex-col gap-1">
          <label for="createEmail">{{ t('staff.fields.email') }}</label>
          <InputText id="createEmail" name="email" type="email" class="w-full" />
          <Message v-if="$form.email?.invalid" severity="error" size="small" variant="simple">
            {{ $form.email?.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="createName">{{ t('staff.fields.name') }}</label>
          <InputText id="createName" name="name" class="w-full" />
        </div>
        <div class="flex flex-col gap-1">
          <label for="createRole">{{ t('staff.fields.role') }}</label>
          <Select
            id="createRole"
            name="role"
            class="w-full"
            :options="roleOptions"
            option-label="label"
            option-value="value"
          />
          <Message v-if="$form.role?.invalid" severity="error" size="small" variant="simple">
            {{ $form.role?.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="createPassword">{{ t('staff.fields.password') }}</label>
          <Password
            id="createPassword"
            name="password"
            class="w-full"
            :feedback="false"
            fluid
            toggle-mask
          />
          <Message v-if="$form.password?.invalid" severity="error" size="small" variant="simple">
            {{ $form.password?.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="createConfirmPassword">{{ t('staff.fields.confirmPassword') }}</label>
          <Password
            id="createConfirmPassword"
            name="confirmPassword"
            class="w-full"
            :feedback="false"
            fluid
            toggle-mask
          />
          <Message
            v-if="$form.confirmPassword?.invalid"
            severity="error"
            size="small"
            variant="simple"
          >
            {{ $form.confirmPassword?.error?.message }}
          </Message>
        </div>
        <div class="flex gap-2">
          <Button
            class="w-full"
            :label="t('common.cancel')"
            text
            @click="showCreateDialog = false"
          />
          <Button
            class="w-full"
            type="submit"
            :label="t('common.create')"
            data-test="staff-create-submit"
          />
        </div>
      </Form>
    </Dialog>

    <Dialog
      v-model:visible="showEditDialog"
      :header="t('staff.dialogs.edit')"
      modal
      dismissable-mask
      class="w-[90vw] sm:w-[600px]"
    >
      <Form
        v-slot="$form"
        :resolver="editResolver"
        :initial-values="editInitialValues"
        class="flex flex-col gap-4"
        @submit="saveEdit"
      >
        <div class="flex flex-col gap-1">
          <label for="editEmail">{{ t('staff.fields.email') }}</label>
          <InputText id="editEmail" name="email" type="email" class="w-full" />
          <Message v-if="$form.email?.invalid" severity="error" size="small" variant="simple">
            {{ $form.email?.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="editName">{{ t('staff.fields.nameShort') }}</label>
          <InputText id="editName" name="name" class="w-full" />
        </div>
        <div class="flex gap-2">
          <Button class="w-full" :label="t('common.cancel')" text @click="showEditDialog = false" />
          <Button class="w-full" type="submit" :label="t('common.save')" />
        </div>
      </Form>
    </Dialog>

    <Dialog
      v-model:visible="showPasswordDialog"
      :header="t('staff.dialogs.password')"
      modal
      dismissable-mask
      class="w-[90vw] sm:w-[600px]"
    >
      <Form
        v-slot="$form"
        :resolver="passwordResolver"
        :initial-values="passwordInitialValues"
        class="flex flex-col gap-4"
        @submit="savePassword"
      >
        <div class="flex flex-col gap-1">
          <label for="newPassword">{{ t('staff.fields.newPassword') }}</label>
          <Password
            id="newPassword"
            name="password"
            class="w-full"
            :feedback="false"
            fluid
            toggle-mask
          />
          <Message v-if="$form.password?.invalid" severity="error" size="small" variant="simple">
            {{ $form.password?.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="confirmNewPassword">{{ t('staff.fields.confirmPassword') }}</label>
          <Password
            id="confirmNewPassword"
            name="confirmPassword"
            class="w-full"
            :feedback="false"
            fluid
            toggle-mask
          />
          <Message
            v-if="$form.confirmPassword?.invalid"
            severity="error"
            size="small"
            variant="simple"
          >
            {{ $form.confirmPassword?.error?.message }}
          </Message>
        </div>
        <div class="flex gap-2">
          <Button
            class="w-full"
            :label="t('common.cancel')"
            text
            @click="showPasswordDialog = false"
          />
          <Button class="w-full" type="submit" :label="t('common.save')" />
        </div>
      </Form>
    </Dialog>

    <Dialog
      v-model:visible="showRoleDialog"
      :header="t('staff.dialogs.role')"
      modal
      dismissable-mask
      class="w-[90vw] sm:w-[600px]"
    >
      <Form
        v-slot="$form"
        :resolver="roleResolver"
        :initial-values="roleInitialValues"
        class="flex flex-col gap-4"
        @submit="saveRole"
      >
        <div class="flex flex-col gap-1">
          <label for="roleSelect">{{ t('staff.fields.role') }}</label>
          <Select
            id="roleSelect"
            name="role"
            class="w-full"
            :options="roleOptions"
            option-label="label"
            option-value="value"
          />
          <Message v-if="$form.role?.invalid" severity="error" size="small" variant="simple">
            {{ $form.role?.error?.message }}
          </Message>
        </div>
        <div class="flex gap-2">
          <Button class="w-full" :label="t('common.cancel')" text @click="showRoleDialog = false" />
          <Button class="w-full" type="submit" :label="t('common.save')" />
        </div>
      </Form>
    </Dialog>
  </section>
</template>

<style scoped>
/* Строка текущего пользователя — голубая подсветка (светлая/тёмная тема). */
:deep(.p-datatable-tbody > tr.staff-self-row > td) {
  background: light-dark(#e7f3fe, #0b2942);
}
</style>
